import dayjs, { type Dayjs } from 'dayjs';
import { wallClockInStore } from './storeTime';

/**
 * How soon a client can book online: the salon's Settings › Booking window
 * (G15; mpgadmin G77; Lena, 5 Oct 2026). The API refuses a client's new
 * booking or reschedule that starts sooner than now plus the salon's notice,
 * whatever this site shows. This is what the booking site offers, so a client
 * isn't offered a time the server will refuse, on any day.
 *
 * Until the public company carries the notice (the API's release, and the
 * CMS passing `clientBookingNoticeHours` on), the site keeps today's 3 hours,
 * and a refusal teaches it the salon's.
 */

/** Today's rule (3 hours), until the salon's own reaches the booking site. */
export const FALLBACK_NOTICE_HOURS = 3;

const HOUR_MS = 3_600_000;

/** The salon's notice from the public company, or null when it isn't there yet. 0 is "none". */
export const noticeOf = (company: any): number | null => {
  const hours = company?.clientBookingNoticeHours;
  return typeof hours === 'number' && Number.isInteger(hours) && hours >= 0 ? hours : null;
};

/**
 * The salon's wall clock at now plus the notice. A slot that starts before it
 * is too soon to book online; with none (0) it is now, so only slots that have
 * passed are. Counted in real hours, as the API counts them.
 */
export const noticeCutoff = (nowMs: number, hours: number): Dayjs => wallClockInStore(nowMs + hours * HOUR_MS);

/**
 * The first day the booking window can leave a time on, as "2026-10-07": the
 * day after `day`, or the day the notice ends when that is later (with 24 or
 * 48 hours). Where the picker goes when a day's times are all too soon.
 */
export const firstDayAfter = (day: string, nowMs: number, hours: number): string => {
  const next = dayjs(day).add(1, 'day').format('YYYY-MM-DD');
  const ends = noticeCutoff(nowMs, hours).format('YYYY-MM-DD');
  return ends > next ? ends : next;
};

/** "1 hour", "3 hours". */
export const noticeLabel = (hours: number) => (hours === 1 ? '1 hour' : `${hours} hours`);

/** The server's own words for a time too soon (mpgadmin `ClientBookingNotice`). */
export const noticeSentence = (hours: number) =>
  hours > 0
    ? `This salon takes online bookings at least ${noticeLabel(hours)} ahead. Please pick a later time or call the shop.`
    : 'That time has already passed. Please pick a later time.';

/** Over a day whose times are all too soon to book online. */
export const nothingLeftText = (hours: number, today: boolean) => {
  const when = today ? 'today' : 'on this day';
  return hours > 0
    ? `This salon takes online bookings at least ${noticeLabel(hours)} ahead, so nothing is left online ${when} — call the shop for urgent bookings.`
    : `Nothing available online ${when} — call the shop for urgent bookings.`;
};

/** On a time that is too soon, for the pointer. */
export const tooSoonTitle = (hours: number) =>
  `Too soon to book online (at least ${noticeLabel(hours)} ahead) — please call the shop for urgent bookings`;

/** The API's refusal code (mpgadmin G77). */
export const BOOKING_NOTICE = 'booking-notice';

export interface NoticeRefusal {
  /** The server's sentence for the client. */
  sentence: string;
  /** The salon's notice, when the refusal says it. */
  hours: number | null;
}

const hoursOf = (value: unknown): number | null =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : null;

const hoursIn = (sentence: unknown): number | null => {
  const m = typeof sentence === 'string' ? /at least (\d+) hours? ahead/.exec(sentence) : null;
  return m ? Number(m[1]) : null;
};

/** The server's sentence, if it is one to show as it is; else ours. */
const sentenceOf = (value: unknown, hours: number | null) => {
  const s = typeof value === 'string' ? value.trim() : '';
  if (s && s.length <= 240 && !/[<>]/.test(s)) return s;
  return hours != null
    ? noticeSentence(hours)
    : 'This salon needs more notice to book online. Please pick a later time or call the shop.';
};

/** The API's error body: `{ error, details: sentence, additionalInfo: { code, hours } }`. */
const fromApi = (raw: any): NoticeRefusal | null => {
  const info = raw?.additionalInfo;
  if (info?.code !== BOOKING_NOTICE) return null;
  const hours = hoursOf(info.hours) ?? hoursIn(raw.details);
  return { sentence: sentenceOf(raw.details, hours), hours };
};

/**
 * Whether a failed booking or reschedule is the salon's booking window, and
 * its words. The CMS passes the API's refusal on in one of three ways:
 * - its own 409 `{ error, code: 'booking-notice' }`, as it does the
 *   reschedule windows (once it knows this code);
 * - the API's body as it is;
 * - today, a 500 whose `details` carries the API's body after a prefix
 *   ("Create booking failed: {…}").
 */
export const noticeRefusalFrom = (body: string): NoticeRefusal | null => {
  let parsed: any;
  try {
    parsed = JSON.parse(body);
  } catch {
    return null;
  }
  if (parsed?.code === BOOKING_NOTICE) {
    const hours = hoursOf(parsed.hours) ?? hoursIn(parsed.error);
    return { sentence: sentenceOf(parsed.error, hours), hours };
  }
  const direct = fromApi(parsed);
  if (direct) return direct;
  const details = typeof parsed?.details === 'string' ? parsed.details : '';
  const at = details.indexOf('{');
  if (at < 0) return null;
  try {
    return fromApi(JSON.parse(details.slice(at)));
  } catch {
    return null;
  }
};

/** A booking or reschedule the salon's booking window refused: nothing was made or moved. */
export class BookingNoticeError extends Error {
  readonly code = BOOKING_NOTICE;
  readonly hours: number | null;

  constructor(refusal: NoticeRefusal) {
    super(refusal.sentence);
    this.name = 'BookingNoticeError';
    this.hours = refusal.hours;
  }
}

/** By its code, not `instanceof`: an error passed through a callback is still known. */
export const isBookingNoticeError = (error: unknown): error is BookingNoticeError =>
  (error as { code?: unknown } | null)?.code === BOOKING_NOTICE;
